import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  onConfirm: () => Promise<void> | void;
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  destructive = true,
  onConfirm,
  loading,
}: Props) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50" style={{ background: 'rgba(2,6,23,0.75)', backdropFilter: 'blur(8px)' }} />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--radius-lg)] border shadow-2xl"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <div className="flex items-start gap-3 p-5">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
              style={{
                background: destructive ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
                color: destructive ? 'var(--danger)' : 'var(--accent)',
              }}
            >
              <AlertTriangle size={18} strokeWidth={1.8} />
            </div>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-sm" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                {title}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs" style={{ color: 'var(--text-secondary)' }}>
                {description}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" aria-label="Cerrar" className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] hover:bg-[var(--surface-2)]" style={{ color: 'var(--text-muted)' }}>
                <X size={12} />
              </button>
            </Dialog.Close>
          </div>
          <div className="flex justify-end gap-2 border-t px-5 py-3" style={{ borderColor: 'var(--border-subtle)' }}>
            <Dialog.Close asChild>
              <Button variant="ghost" size="sm">
                {cancelText}
              </Button>
            </Dialog.Close>
            <Button
              variant={destructive ? 'danger' : 'primary'}
              size="sm"
              disabled={loading}
              onClick={async () => {
                await onConfirm();
                onOpenChange(false);
              }}
            >
              {confirmText}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
